import React, { useState, useEffect, useCallback } from "react";
import {
  getRole,
  roleLabel,
  roleClass,
  roleDescription,
  roleIcon,
  SIDE_LABELS,
} from "../roles";

export default function RoleCardsModal({
  players = [],
  onClose,
  // Коли true — картка одразу відкрита на бік з роллю, без проміжного
  // кроку "передайте пристрій гравцю". Використовується, наприклад, коли
  // священник чи адвокат щойно перевірив гравця: ведучий одразу показує
  // роль обраного гравця тією ж карткою, що й при роздачі ролей, але без
  // потреби тапати "Показати мою роль".
  initialRevealed = false,
  // Опційний заголовок замість лічильника "Гравець N з total" — зручно
  // для контексту перевірки/сповіді, а не роздачі ролей на старті гри.
  title = null,
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [revealed, setRevealed] = useState(initialRevealed);

  const total = players.length;
  const player = players[currentIndex] || null;
  const role = player ? getRole(player.role) : null;

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setRevealed(initialRevealed);
    }
  }, [currentIndex, initialRevealed]);

  const goToNext = useCallback(() => {
    if (currentIndex < total - 1) {
      setCurrentIndex((prev) => prev + 1);
      setRevealed(initialRevealed);
    } else {
      onClose();
    }
  }, [currentIndex, total, onClose, initialRevealed]);

  const toggleReveal = useCallback(() => {
    setRevealed((prev) => !prev);
  }, []);

  // Підтримка клавіатури: стрілки для гортання, пробіл/Enter для відкриття, Esc для закриття
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      } else if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggleReveal();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToPrev, goToNext, toggleReveal, onClose]);

  if (!player || !role) return null;

  const isFirst = currentIndex === 0;
  const isLast = currentIndex === total - 1;
  const teamLabel = SIDE_LABELS[role.side] || role.side;

  return (
    <div className="modal-backdrop role-cards-backdrop" role="dialog" aria-modal="true">
      <div className="role-cards-container">
        <header className="role-cards-header">
          <div className="role-cards-counter">
            {title ? (
              <span className="role-cards-counter-current">{title}</span>
            ) : (
              <>
                <span className="role-cards-counter-current">Гравець {currentIndex + 1}</span>
                <span className="role-cards-counter-total"> з {total}</span>
              </>
            )}
          </div>
          <button
            type="button"
            className="role-cards-close-btn"
            onClick={onClose}
            aria-label="Закрити роздачу ролей"
          >
            ×
          </button>
        </header>

        <div className="role-cards-deck">
          <div className="role-card-item">
            {/* Рендеримо тільки одну сторону за раз */}
            {!revealed ? (
              /* Сорочка картки */
              <div className="role-card-face role-card-front" onClick={toggleReveal} role="button" tabIndex={0} aria-label="Відкрити роль">
              <div className="role-card-emblem">🎭</div>
              <p className="role-card-prompt">Передайте пристрій гравцю:</p>
              <h2 className="role-card-player-name">{player.name || `Гравець #${currentIndex + 1}`}</h2>
              <p className="role-card-hint">
                Переконайтеся, що інші не дивляться на екран, і натисніть, щоб дізнатися роль.
              </p>
              <button
                type="button"
                className="btn btn-primary btn-reveal"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReveal();
                }}
              >
                👁 Показати мою роль
              </button>
            </div>
            ) : (
            /* Відкрита картка з роллю */
            <div className="role-card-face role-card-back">
              <div className="role-card-back-header">
                <span className="role-card-for-name">{player.name}</span>
                <span className={`role-chip role-chip-${role.side}`}>Команда: {teamLabel}</span>
              </div>

              <div className="role-card-icon">{roleIcon(player.role)}</div>

              <h2 className={`role-card-title ${roleClass(player.role)}`}>
                {roleLabel(player.role)}
              </h2>

              <p className="role-card-description">
                {roleDescription(player.role)}
              </p>

              <div className="role-card-action-badge">
                {role.nightAction ? (
                  <span>🌙 Активна вночі: прокидається під час свого ходу</span>
                ) : (
                  <span>☀️ Пасивна роль: уночі спить разом з іншими</span>
                )}
              </div>

              <button
                type="button"
                className="btn btn-ghost btn-hide"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReveal();
                }}
              >
                🙈 Сховати роль
              </button>
            </div>
            )}
          </div>
        </div>

        <footer className="role-cards-footer">
          <button
            type="button"
            className="btn btn-ghost btn-nav"
            onClick={goToPrev}
            disabled={isFirst}
          >
            ← Назад
          </button>

          <div className="role-cards-dots" aria-hidden="true">
            {players.map((_, idx) => (
              <span
                key={idx}
                className={`role-cards-dot ${idx === currentIndex ? "role-cards-dot-active" : ""}`}
                onClick={() => {
                  setCurrentIndex(idx);
                  setRevealed(initialRevealed);
                }}
              />
            ))}
          </div>

          <button
            type="button"
            className={`btn ${isLast ? "btn-accent" : "btn-primary"} btn-nav`}
            onClick={goToNext}
          >
            {isLast ? "Завершити ✓" : "Вперед →"}
          </button>
        </footer>
      </div>
    </div>
  );
}
